import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import DashboardLayout from '@/components/feature/DashboardLayout';
import {
  fmtCentavos,
  statusCliente,
  mapSubscriptionStatus,
  usePlanos,
  type PlanoDaPlataforma,
} from '@/pages/admin/shared';

interface ProfileFromDB {
  id: string;
  tipo: string;
  nome: string;
  cnpj: string | null;
  razao_social: string | null;
  nome_responsavel: string | null;
  email: string;
  telefone: string | null;
  plano_id: string | null;
  subscription_status: string | null;
  account_id: string | null;
  cep: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  estado: string | null;
  created_at: string | null;
  stripe_customer_id: string | null;
}

interface ClienteListItem {
  id: string;
  nome: string;
  responsavel: string;
  email: string;
  tipo: 'pf' | 'pj';
  plano: string;
  planoId: string | null;
  valorMensal: number;
  status: 'ativo' | 'inativo' | 'cancelado';
  cidade: string;
  estado: string;
  condominios: number;
  dataInicio: string;
}

function toClienteListItem(
  profile: ProfileFromDB,
  condominioCount: number,
  planos: Record<string, PlanoDaPlataforma>,
): ClienteListItem {
  const st = mapSubscriptionStatus(profile.subscription_status);
  const plano = (profile.plano_id && planos[profile.plano_id]) || null;

  const displayName =
    profile.tipo === 'pj'
      ? profile.razao_social || profile.nome || profile.email.split('@')[0]
      : profile.nome || profile.email.split('@')[0];

  return {
    id: profile.id,
    nome: displayName,
    responsavel: profile.nome_responsavel || profile.nome || profile.email.split('@')[0],
    email: profile.email,
    tipo: (profile.tipo as 'pf' | 'pj') || 'pf',
    plano: plano?.nome || 'Sem plano',
    planoId: profile.plano_id,
    valorMensal: plano?.preco || 0,
    status: st.status,
    cidade: profile.cidade || '—',
    estado: profile.estado || '—',
    condominios: condominioCount,
    dataInicio: profile.created_at?.split('T')[0] || '',
  };
}

export default function AdminClientesPage() {
  const { isSuperuser } = useAuth();
  const navigate = useNavigate();
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroPlano, setFiltroPlano] = useState('');
  const [clientes, setClientes] = useState<ClienteListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { planos, porId: planosPorId, carregando: carregandoPlanos } = usePlanos();

  const fetchClientes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .neq('email', 'admin@centralcheck.com.br')
        .order('created_at', { ascending: false });

      if (profilesError) throw profilesError;

      const { data: condominios, error: condError } = await supabase
        .from('condominios')
        .select('account_id');

      if (condError) throw condError;

      const condCountByAccount: Record<string, number> = {};
      (condominios || []).forEach((c) => {
        if (c.account_id) {
          condCountByAccount[c.account_id] = (condCountByAccount[c.account_id] || 0) + 1;
        }
      });

      const mapped = (profiles || []).map((p: ProfileFromDB) =>
        toClienteListItem(p, condCountByAccount[p.account_id || ''] || 0, planosPorId),
      );
      setClientes(mapped);
    } catch (err: any) {
      console.error('Erro ao carregar clientes:', err);
      // Sem lista de mentira: a falha aparece como falha. Mostrar doze clientes
      // inventados quando a consulta quebra é pior que mostrar o erro.
      setClientes([]);
      setError(err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [planosPorId]);

  useEffect(() => {
    if (!isSuperuser) {
      setLoading(false);
      return;
    }
    // Espera o catálogo de planos: sem ele todo cliente sairia como "Sem plano".
    if (carregandoPlanos) return;
    void fetchClientes();
  }, [isSuperuser, carregandoPlanos, fetchClientes]);

  const filtrados = useMemo(() => {
    return clientes.filter((c) => {
      const matchBusca =
        c.nome.toLowerCase().includes(busca.toLowerCase()) ||
        c.responsavel.toLowerCase().includes(busca.toLowerCase()) ||
        c.email.toLowerCase().includes(busca.toLowerCase());
      const matchStatus = filtroStatus ? c.status === filtroStatus : true;
      const matchPlano = filtroPlano ? c.planoId === filtroPlano : true;
      return matchBusca && matchStatus && matchPlano;
    });
  }, [clientes, busca, filtroStatus, filtroPlano]);

  const totalClientes = clientes.length;
  const clientesAtivos = clientes.filter((c) => c.status === 'ativo').length;
  const clientesCancelados = clientes.filter((c) => c.status === 'cancelado').length;
  const receitaFiltrada = filtrados
    .filter((c) => c.status === 'ativo')
    .reduce((s, c) => s + c.valorMensal, 0);

  if (!isSuperuser) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <i className="ri-lock-line text-red-600 text-2xl"></i>
          </div>
          <h2 className="text-lg font-bold text-foreground-900 mb-2">Acesso Restrito</h2>
          <p className="text-sm text-foreground-500 text-center max-w-md">
            Esta área é reservada para o superusuário administrador da plataforma. Faça login com as
            credenciais de administrador para acessar.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl md:text-2xl font-bold text-foreground-900">Clientes</h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-accent-500 text-background-50">
              Super Admin
            </span>
          </div>
          <p className="text-sm text-foreground-500">
            Gerencie os clientes da plataforma e acompanhe seus dados.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex items-center gap-3 text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-xl"></i>
              <span className="text-sm">Carregando clientes...</span>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {/* Cards resumo */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                {
                  label: 'Total clientes',
                  valor: totalClientes,
                  icone: 'ri-building-line',
                  corIcon: 'text-primary-600',
                  corBg: 'bg-primary-50',
                },
                {
                  label: 'Clientes ativos',
                  valor: clientesAtivos,
                  icone: 'ri-check-double-line',
                  corIcon: 'text-green-600',
                  corBg: 'bg-green-50',
                },
                {
                  label: 'Cancelados',
                  valor: clientesCancelados,
                  icone: 'ri-close-circle-line',
                  corIcon: 'text-red-600',
                  corBg: 'bg-red-50',
                },
              ].map((card) => (
                <div
                  key={card.label}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-4"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div
                      className={`w-8 h-8 rounded-lg ${card.corBg} flex items-center justify-center`}
                    >
                      <i className={`${card.icone} ${card.corIcon} text-sm`}></i>
                    </div>
                    <p className="text-xs font-medium text-foreground-500">{card.label}</p>
                  </div>
                  <p className="text-2xl font-bold text-foreground-900">{card.valor}</p>
                </div>
              ))}
            </div>

            {/* Filtros */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1 max-w-sm">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <i className="ri-search-line text-foreground-400 text-sm"></i>
                </div>
                <input
                  type="text"
                  placeholder="Buscar cliente ou responsável..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
                />
              </div>
              <select
                value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value)}
                className="px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              >
                <option value="">Todos os status</option>
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
                <option value="cancelado">Cancelado</option>
              </select>
              <select
                value={filtroPlano}
                onChange={(e) => setFiltroPlano(e.target.value)}
                className="px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
              >
                <option value="">Todos os planos</option>
                {planos.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome}
                  </option>
                ))}
              </select>
            </div>

            {/* Erro */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-600"></i>
                  <span className="text-sm text-red-700">
                    Erro ao carregar os clientes: {error}
                  </span>
                </div>
                <button
                  onClick={fetchClientes}
                  className="whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors cursor-pointer"
                >
                  <i className="ri-refresh-line"></i>
                  Tentar novamente
                </button>
              </div>
            )}

            {/* Tabela */}
            <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-background-200/70 bg-background-100/50">
                      <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                        Cliente
                      </th>
                      <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap hidden md:table-cell">
                        Tipo
                      </th>
                      <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap hidden md:table-cell">
                        Plano
                      </th>
                      <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap hidden lg:table-cell">
                        Local
                      </th>
                      <th className="text-right text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                        MRR
                      </th>
                      <th className="text-center text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                        Cond.
                      </th>
                      <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                        Status
                      </th>
                      <th className="text-right text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200/50">
                    {filtrados.length === 0 ? (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-4 py-12 text-center text-sm text-foreground-400"
                        >
                          Nenhum cliente encontrado.
                        </td>
                      </tr>
                    ) : (
                      filtrados.map((cli) => {
                        const stInfo = statusCliente[cli.status] || statusCliente.inativo;
                        return (
                          <tr key={cli.id} className="hover:bg-background-100/30 transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                                  <span className="text-xs font-bold text-primary-700">
                                    {cli.nome
                                      .split(' ')
                                      .slice(0, 2)
                                      .map((n) => n[0])
                                      .join('')}
                                  </span>
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-foreground-800 truncate">
                                    {cli.nome}
                                  </p>
                                  <p className="text-xs text-foreground-500 truncate">
                                    {cli.responsavel}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 hidden md:table-cell">
                              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 whitespace-nowrap">
                                {cli.tipo === 'pj' ? 'PJ' : 'PF'}
                              </span>
                            </td>
                            <td className="px-4 py-3 hidden md:table-cell">
                              <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">
                                {cli.plano}
                              </span>
                              <p className="text-xs text-foreground-500">
                                {fmtCentavos(cli.valorMensal)}/mês
                              </p>
                            </td>
                            <td className="px-4 py-3 hidden lg:table-cell">
                              <span className="text-sm text-foreground-600">
                                {cli.cidade}, {cli.estado}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="text-sm font-semibold text-foreground-800">
                                {fmtCentavos(cli.valorMensal)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-sm text-foreground-600">{cli.condominios}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${stInfo.cor}`}
                              >
                                {stInfo.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                onClick={() => navigate(`/admin/clientes/${cli.id}`)}
                                className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-primary-600 transition-colors ml-auto cursor-pointer"
                                title="Ver detalhes"
                              >
                                <i className="ri-eye-line text-sm"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-background-200/70 bg-background-100/30">
                      <td
                        colSpan={4}
                        className="px-4 py-3 text-sm font-semibold text-foreground-700 text-right"
                      >
                        Receita dos ativos exibidos:
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm font-bold text-green-700">
                          {fmtCentavos(receitaFiltrada)}
                        </span>
                      </td>
                      <td colSpan={3}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
